import { ActionFunction, redirect } from 'react-router-dom';
import { Store } from 'redux';

// Sentry
import * as Sentry from '@sentry/react';

// GQL
import { formNaming } from '../../../../../components/forms/NewCommunicationForm/constants';

// Selector
import { getCurrentIndexTabFormByTab } from '../../../../../state/selectors/ui/tabs';
import { selectedOT } from '../../../../../state/selectors/ui/modal';
// Store

import { RootState } from '../../../../../state/configureStore';
// Actions
import { passData } from './ActionUtils/passData';
import { saveData } from './ActionUtils/saveData';
import { tabsType } from '../../../../../state/reducers/ui/tabs';
import { indexTabFormReset } from '../../../../../state/actions/ui/tabs';
// Utils
import { getModalKeyByTab } from '../../../../../utils/getModalKeyByTab';
import { isOfflineQueuedError } from '../../../../../offline/isOfflineQueuedError';
import { communicationQueuedRedirect } from '../../communicationQueuedRedirect';
import { showSuccessToast, showBackendErrorToast } from '../../../../../utils/toasts';
import i18n from '../../../../../i18n';
import {
  releaseCommunicationCreationDraft,
  trustedCommunicationSelection,
} from '../../../../../utils/communicationCreationDraft';

function cleanupCommunicationSessionStorage() {
  // Las copias del alta y su dueño (`new-communication-owner`).
  releaseCommunicationCreationDraft();
  sessionStorage.removeItem(formNaming.COMMUNICATION_NAVIGATION_CONTEXT);
}

export const ActionNewCommunicationSummary: (
  store: Store<RootState>
) => ActionFunction =
  (store) =>
  async ({ request, params }) => {
    const formData = await request.formData();
    const intent = formData.get('intent');
    
    const tabSelected = getCurrentIndexTabFormByTab(tabsType.COMMUNICATION)(
      store.getState() as RootState
    );
    
    console.log('[ActionNewCommunicationSummary] Tab selected:', tabSelected);
    
    // Get the correct modal key based on tab index
    const modalKey = getModalKeyByTab(formNaming.NEW_COMMUNICATION_FORM, tabSelected);
    
    console.log('[ActionNewCommunicationSummary] Using modal key:', modalKey);
    
    const storedDataOTSelected = selectedOT(modalKey)(
      store.getState() as RootState
    );
    
    console.log('[ActionNewCommunicationSummary] Stored OT data:', storedDataOTSelected);

    // Lo que se ENSEÑA en el resumen y a dónde se vuelve si se encola: solo la
    // OT/campaña del modal que vale para esta alta (la de la URL o la elegida a
    // mano; revisión de la Task 12a). `saveData` recibe la de Redux tal cual y
    // aplica la misma regla, suelta la ajena y, en la sección suelta, falla.
    const trustedSelection = trustedCommunicationSelection(
      modalKey,
      storedDataOTSelected,
      params.id
    );

    if (intent === 'pass-data') {
      try {
        return passData({
          formData,
          tabSelected,
          storedDataOTSelected: trustedSelection,
        });
      } catch (err) {
        Sentry.captureException(err);
        console.log('Error:', err);
      }
    }
    if (intent === 'save-data') {
      // Fuera del `try`: el `catch` lo necesita para decidir a dónde volver, y
      // para entonces la limpieza ya ha borrado el borrador de sessionStorage.
      const dataFormRaw = sessionStorage.getItem(
        formNaming.NEW_COMMUNICATION_FORM
      );
      try {
        return await saveData({
          dataFormRaw,
          tabSelected,
          storedDataOTSelected,
          store,
          // El `:id` del padre en los árboles de OT y de campaña (la barrera
          // del alta lo exige); sin `:id` es la sección suelta.
          params,
        });
      } catch (err) {
        // Bloque C-7.3: si la mutation se encoló offline, OfflineQueueLink
        // lanza un Error que llegaba al ErrorBoundary del router. Aquí lo
        // tratamos como éxito: limpiar form, avisar al banner y redirigir.
        if (isOfflineQueuedError(err)) {
          showSuccessToast(i18n.t('modals:changeState.queuedOffline'));
          cleanupCommunicationSessionStorage();
          store.dispatch(indexTabFormReset(tabsType.COMMUNICATION));
          window.dispatchEvent(
            new CustomEvent('offline-communication-queued')
          );
          // Al PADRE, no al módulo (2026-09-11): quien creaba la comunicación
          // dentro de una OT o una campaña acababa en el listado de
          // comunicaciones, como si hubiera empezado de cero.
          return redirect(
            communicationQueuedRedirect({
              dataFormRaw,
              tabSelected,
              storedDataOTSelected: trustedSelection,
            }),
          );
        }
        Sentry.captureException(err);
        console.log('> CommunicationSummary - error creating:', err);
        showBackendErrorToast(err, i18n.t('toasts:error.generic'));
      }
    }
    return null;
  };
